import { useCallback, useEffect, useRef, useState, Dispatch, SetStateAction } from 'react';
import { lessonProgressService } from './LessonProgressService';

// Drop-in replacement for useState that also persists to localStorage (see
// LessonProgressService), so a section's in-progress state survives a full remount instead of
// resetting. lessonId is optional so this is safe to call on the public share page (readOnly,
// no owner to persist progress for) — undefined simply skips persistence entirely, behaving
// exactly like plain useState.
//
// The value is tagged with the lessonId it belongs to. If the same mounted component is re-used
// for a different lesson (e.g. /lessons/1 → /lessons/2 via history, which keeps the route's
// component instance), the new lesson's saved value is loaded instead of the old in-memory value
// being written under the new lesson's key. Only values changed through the setter are saved —
// mounting never writes anything back.
export function useLessonPersistedState<T>(lessonId: number | undefined, field: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
    const load = (id: number | undefined): T => (id ? lessonProgressService.loadField(id, field, initial) : initial);
    const [state, setState] = useState<{ id: number | undefined; value: T }>(() => ({ id: lessonId, value: load(lessonId) }));
    const dirty = useRef(false);

    let current = state;
    if (state.id !== lessonId) {
        // Adjusting state during render (React's documented pattern for resetting on a prop
        // change) — the stale lesson's value is never rendered or saved.
        current = { id: lessonId, value: load(lessonId) };
        dirty.current = false;
        setState(current);
    }

    const setValue = useCallback<Dispatch<SetStateAction<T>>>((action) => {
        dirty.current = true;
        setState((prev) => ({
            id: prev.id,
            value: typeof action === 'function' ? (action as (p: T) => T)(prev.value) : action,
        }));
    }, []);

    useEffect(() => {
        if (dirty.current && state.id && state.id === lessonId) {
            lessonProgressService.saveField(state.id, field, state.value);
        }
    }, [state, lessonId, field]);

    return [current.value, setValue];
}
